import { useEffect, useRef } from 'react';

import { useToast } from '../../hooks/useToast';
import type { FilterAction } from '../engine/filterReducer';
import {
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type MultiFilter,
  type PrimarySelection,
  type TreeBranches,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import {
  getGeographyDefaultSelectionsForTaxonomy,
  repairCrossTaxonomyGeographyDefaults,
} from '../utils/getGeographyDefaultSelectionsForTaxonomy';

// Hardcoded default primary selections for when all primary filters are
// incompatible with the new geography mode and need to be replaced.

const levelIdFromSelectionId = (
  selectionId: string,
  selectionListIds: TreeFilter['selectionListIds']
): TreeSelection['levelId'] | undefined => {
  const segments = String(selectionId).split('_');
  if (segments.length <= 1) {
    return undefined;
  }
  const depth = segments.length - 1;
  return selectionListIds[depth];
};

const recalcGeographyTreeLevelIds = (
  treeFilter: TreeFilter,
  selectionListIds: TreeFilter['selectionListIds']
): TreeFilter => {
  const mapSelection = (sel: TreeSelection): TreeSelection => {
    const levelId = levelIdFromSelectionId(sel.id, selectionListIds);
    if (!levelId) return sel;
    return { ...sel, levelId };
  };

  const selections = repairCrossTaxonomyGeographyDefaults(
    treeFilter.selections ?? [],
    selectionListIds
  ).map(mapSelection);
  const primarySelections =
    treeFilter.primarySelections &&
    repairCrossTaxonomyGeographyDefaults(
      treeFilter.primarySelections,
      selectionListIds
    ).map(mapSelection);

  return {
    ...treeFilter,
    selectionListIds,
    selections,
    ...(primarySelections ? { primarySelections } : {}),
  };
};

/**
 * Applies geography mode (selection list IDs + branch labels + levelId derived
 * from id depth) to a single geography tree filter.
 */
const applyGeographyTaxonomyToTreeFilter = (
  treeFilter: TreeFilter,
  selectionListIds: TreeFilter['selectionListIds'],
  branches: TreeBranches
): TreeFilter => {
  const withLevelIds = recalcGeographyTreeLevelIds(
    treeFilter,
    selectionListIds
  );

  return {
    ...withLevelIds,
    branches,
  };
};

/**
 * Adapts the active filter map so the geography filter reflects the current
 * geography mode (selection list IDs + branch labels). Does NOT strip
 * incompatible selections — that is handled by `useGeographyTaxonomyChangeEffect`.
 */
export const applyGeographyTaxonomyToFilterMap = (
  filterMap: FilterSelectionsMap,
  selectionListIds: TreeFilter['selectionListIds'],
  branches: TreeBranches
): FilterSelectionsMap => {
  const map = new Map(filterMap);

  const geoFilter = map.get(FilterId.GEOGRAPHY) as TreeFilter | undefined;
  if (geoFilter) {
    map.set(
      FilterId.GEOGRAPHY,
      applyGeographyTaxonomyToTreeFilter(geoFilter, selectionListIds, branches)
    );
  }

  const multiGeo = map.get(FilterId.MULTI_GEOGRAPHY) as
    | MultiFilter<TreeFilter>
    | undefined;
  if (
    multiGeo &&
    multiGeo.type === FilterType.MULTI &&
    Array.isArray(multiGeo.instances)
  ) {
    const instances = multiGeo.instances.map((inst) =>
      inst.type === FilterType.TREE
        ? applyGeographyTaxonomyToTreeFilter(inst, selectionListIds, branches)
        : inst
    );
    map.set(FilterId.MULTI_GEOGRAPHY, { ...multiGeo, instances });
  }

  return map;
};

const stripIncompatibleSelectionsFromGeographyTree = (
  treeFilter: TreeFilter,
  selectionListIds: TreeFilter['selectionListIds'],
  defaultPrimarySelections: PrimarySelection[]
): TreeFilter | null => {
  // A selection is incompatible when its level isn't in the taxonomy being
  // switched to — covering msa-vs-state, city-vs-county, and any level a
  // future taxonomy drops, without enumerating the pairs.
  const validLevelIds = new Set<string>(selectionListIds);
  const isIncompatible = (s: { levelId?: string }) =>
    s.levelId !== undefined && !validLevelIds.has(s.levelId);

  const selections = treeFilter.selections ?? [];
  const primarySelections = treeFilter.primarySelections ?? [];

  const hasIncompatibleSelections = selections.some(isIncompatible);
  const hasIncompatiblePrimary = primarySelections.some(isIncompatible);

  if (!hasIncompatibleSelections && !hasIncompatiblePrimary) {
    return null;
  }

  const strippedSelections = selections.filter((s) => !isIncompatible(s));
  const strippedPrimary = primarySelections.filter((s) => !isIncompatible(s));

  let finalPrimary: PrimarySelection[] | undefined;
  if (strippedPrimary.length > 0) {
    finalPrimary = strippedPrimary;
  } else if (hasIncompatiblePrimary) {
    finalPrimary = defaultPrimarySelections;
  }

  return {
    ...treeFilter,
    selections: finalPrimary ?? strippedSelections,
    primarySelections: finalPrimary,
  };
};

/**
 * When the geography taxonomy changes, strips selections whose level the new
 * taxonomy doesn't have, dispatches a REPLACE_FILTERS action to persist
 * changes, and shows a toast notification.
 */
export const useGeographyTaxonomyChangeEffect = ({
  geographySelectionListIds,
  geographyTaxonomyId,
  normalizedActiveFilterMap,
  filterStateActiveKey,
  dispatch,
}: {
  geographySelectionListIds: TreeFilter['selectionListIds'];
  geographyTaxonomyId: number;
  normalizedActiveFilterMap: FilterSelectionsMap;
  filterStateActiveKey: string | undefined;
  dispatch: React.Dispatch<FilterAction>;
}) => {
  const toast = useToast();

  // Keyed on the LEVEL SET, not the taxonomy id. The id changes as soon as the
  // preference does, but the levels arrive a render later with the new tree —
  // firing on the id would strip against the OUTGOING taxonomy's levels and
  // leave the incompatible selections in place. The level set changes exactly
  // once the new taxonomy is actually known.
  const selectionListIdsKey = geographySelectionListIds.join(',');
  const prevSelectionListIdsKeyRef = useRef(selectionListIdsKey);

  // Store the filter map in a ref so the effect always reads the latest
  // snapshot without needing it in the dependency array (which would fire
  // on every filter change, not just geography taxonomy changes).
  const filterMapRef = useRef(normalizedActiveFilterMap);
  filterMapRef.current = normalizedActiveFilterMap;

  const selectionListIdsRef = useRef(geographySelectionListIds);
  selectionListIdsRef.current = geographySelectionListIds;

  // Only the FIRST default becomes a page entity. The full set is what gets
  // written to the geography DefaultFilters preference on a taxonomy change;
  // seeding all five here would replace one chip with five on every switch.
  const defaultPrimarySelections = getGeographyDefaultSelectionsForTaxonomy(
    geographyTaxonomyId
  ).slice(0, 1);
  const defaultPrimarySelectionsRef = useRef(defaultPrimarySelections);
  defaultPrimarySelectionsRef.current = defaultPrimarySelections;

  // biome-ignore lint/correctness/useExhaustiveDependencies: only fire when the geography level set changes; other deps are stable or read via refs.
  useEffect(() => {
    if (prevSelectionListIdsKeyRef.current === selectionListIdsKey) return;
    prevSelectionListIdsKeyRef.current = selectionListIdsKey;

    const updatedFilterMap = new Map(filterMapRef.current);
    let didStrip = false;

    const geoFilter = updatedFilterMap.get(FilterId.GEOGRAPHY) as
      | TreeFilter
      | undefined;
    if (geoFilter) {
      const stripped = stripIncompatibleSelectionsFromGeographyTree(
        geoFilter,
        selectionListIdsRef.current,
        defaultPrimarySelectionsRef.current
      );
      if (stripped) {
        updatedFilterMap.set(FilterId.GEOGRAPHY, stripped);
        didStrip = true;
      }
    }

    const multiGeo = updatedFilterMap.get(FilterId.MULTI_GEOGRAPHY) as
      | MultiFilter<TreeFilter>
      | undefined;
    if (
      multiGeo &&
      multiGeo.type === FilterType.MULTI &&
      Array.isArray(multiGeo.instances)
    ) {
      let anyInstanceStripped = false;
      const newInstances = multiGeo.instances.map((inst) => {
        if (inst.type !== FilterType.TREE) return inst;
        const stripped = stripIncompatibleSelectionsFromGeographyTree(
          inst,
          selectionListIdsRef.current,
          defaultPrimarySelectionsRef.current
        );
        if (stripped) {
          anyInstanceStripped = true;
          return stripped;
        }
        return inst;
      });
      if (anyInstanceStripped) {
        updatedFilterMap.set(FilterId.MULTI_GEOGRAPHY, {
          ...multiGeo,
          instances: newInstances,
        });
        didStrip = true;
      }
    }

    if (!didStrip) return;

    const activeKey = filterStateActiveKey ?? FilterId.GEOGRAPHY;

    dispatch({
      type: 'REPLACE_FILTERS',
      payload: {
        filterMap: updatedFilterMap,
        consistencyKey: activeKey,
      },
    });

    toast({
      title: 'Incompatible geography filters removed',
      description:
        'Selections from the previous geography taxonomy are not available in the one you chose.',
      status: 'info',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
  }, [selectionListIdsKey]);
};
